/**
 * Data layer for `coupon-code-widget`. Thin typed fetch wrappers over /api/v1/coupon-code-widget.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface CouponCodeWidgetRow {
	id: string;
	tenant_id: string;
	coupon_code: string;
	period_start: string;
	period_end: string;
	redemptions: number;
	revenue_cents: number;
	unique_users: number;
	avg_order_cents: number | null;
	created_at: string;
}

export type CouponSort = "redemptions" | "revenue" | "unique_users";

export interface ListCouponCodeWidgetResponse {
	items: CouponCodeWidgetRow[];
	sort: CouponSort;
	limit: number;
}

export interface ListCouponCodeWidgetParams {
	period_start?: string;
	period_end?: string;
	sort?: CouponSort;
	limit?: number;
}

export interface CreateCouponCodeWidgetBody {
	coupon_code: string;
	period_start: string;
	period_end: string;
	redemptions?: number;
	revenue_cents?: number;
	unique_users?: number;
}

export interface CreateCouponCodeWidgetResponse {
	ok: true;
	avg_order_cents: number | null;
}

const BASE = "/api/v1/coupon-code-widget";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(
	params: ListCouponCodeWidgetParams = {},
): Promise<ListCouponCodeWidgetResponse> {
	const qs = new URLSearchParams();
	if (params.period_start) qs.set("period_start", params.period_start);
	if (params.period_end) qs.set("period_end", params.period_end);
	if (params.sort) qs.set("sort", params.sort);
	if (typeof params.limit === "number") qs.set("limit", String(params.limit));
	const url = qs.toString() ? `${BASE}?${qs.toString()}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListCouponCodeWidgetResponse>(res);
}

export async function create(
	body: CreateCouponCodeWidgetBody,
): Promise<CreateCouponCodeWidgetResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateCouponCodeWidgetResponse>(res);
}

export const couponCodeWidgetQueryKeys = {
	all: ["coupon-code-widget"] as const,
	list: (params: ListCouponCodeWidgetParams = {}) =>
		[...couponCodeWidgetQueryKeys.all, "list", params] as const,
};
